import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { parseStryktips, type ParsedTips } from '../lib/stryktipsParse'

interface Question {
  id: string
  q_no: number
  label: string
  opt_1: string
  opt_x: string
  opt_2: string
  answer: '1' | 'X' | '2' | null
}

const OPTIONS: Array<'1' | 'X' | '2'> = ['1', 'X', '2']

/**
 * Rättarpanelen.
 *
 * Facit matas in fråga för fråga allteftersom — flera frågor kräver mänskligt
 * omdöme (birdies sista ronden, vilken boll någon spelar på söndagen) och går
 * inte att auto-rätta. Varje klick skriver direkt och triggar omräkningen i
 * databasen, så ställningen är alltid aktuell utan en spara-knapp att glömma.
 *
 * Frågorna går att skriva om i efterhand. Rättigheten har funnits sedan 0024
 * men gränssnittet erbjöd bara att skapa arton tomma — en stavfel i fråga
 * elva krävde alltså SQL. Redigeringen ligger bakom en knapp: det vanliga
 * under tävlingsveckan är att rätta, inte att skriva om, och fälten ska inte
 * stå och konkurrera med facitknapparna.
 */
export function StryktipsFacit() {
  const { id = '' } = useParams()
  const { profile } = useAuth()

  const [questions, setQuestions] = useState<Question[]>([])
  const [tiebreakLabel, setTiebreakLabel] = useState<string | null>(null)
  const [tiebreakActual, setTiebreakActual] = useState<string>('')
  const [tournamentName, setTournamentName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingTiebreak, setSavingTiebreak] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [savedQ, setSavedQ] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)
  const [entryCount, setEntryCount] = useState(0)
  const [labelDraft, setLabelDraft] = useState('')
  const [paste, setPaste] = useState('')
  const [parsed, setParsed] = useState<ParsedTips | null>(null)
  const [applying, setApplying] = useState(false)
  const [confirmApply, setConfirmApply] = useState(false)

  const load = useCallback(async () => {
    const [t, q, f, e] = await Promise.all([
      supabase
        .from('tournaments')
        .select('name, stryktips_tiebreak_label, stryktips_tiebreak_actual')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('stryktips_questions')
        .select('id, q_no, label, opt_1, opt_x, opt_2')
        .eq('tournament_id', id)
        .order('q_no'),
      supabase.from('stryktips_facit').select('question_id, answer'),
      // Har någon redan tippat spelar det roll om en fråga skrivs om: deras
      // 1/X/2 står kvar men kan komma att betyda något annat.
      supabase
        .from('stryktips_entries')
        .select('id', { count: 'exact', head: true })
        .eq('tournament_id', id),
    ])

    setEntryCount(e.count ?? 0)

    if (t.data) {
      setTournamentName(t.data.name as string)
      setTiebreakLabel((t.data.stryktips_tiebreak_label as string) ?? null)
      setLabelDraft((t.data.stryktips_tiebreak_label as string) ?? '')
      setTiebreakActual(
        t.data.stryktips_tiebreak_actual === null ? '' : String(t.data.stryktips_tiebreak_actual),
      )
    }
    if (q.error) setError(q.error.message)

    const facit = new Map(
      ((f.data ?? []) as Array<{ question_id: string; answer: '1' | 'X' | '2' }>).map((x) => [
        x.question_id,
        x.answer,
      ]),
    )

    setQuestions(
      ((q.data ?? []) as Question[]).map((x) => ({ ...x, answer: facit.get(x.id) ?? null })),
    )
    setLoading(false)
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  if (profile && profile.role === 'deltagare') {
    return (
      <div className="page">
        <div className="notice notice--error">
          Rättarpanelen är för rättare och admin.
        </div>
      </div>
    )
  }

  async function setFacit(q: Question, answer: '1' | 'X' | '2' | null) {
    setError(null)
    // Optimistiskt: klicket ska kännas direkt, omräkningen sker i databasen.
    setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, answer } : x)))

    const { error } =
      answer === null
        ? await supabase.from('stryktips_facit').delete().eq('question_id', q.id)
        : await supabase
            .from('stryktips_facit')
            .upsert({ question_id: q.id, answer, set_by: profile?.id }, { onConflict: 'question_id' })

    if (error) {
      setError(error.message)
      void load()
    }
  }

  async function saveTiebreak() {
    setSavingTiebreak(true)
    // Via RPC: utslagsfacit ligger på tournaments, som bara admin får skriva.
    // Rättaren behöver just den kolumnen, och RLS kan inte begränsa per kolumn.
    const { error } = await supabase.rpc('set_stryktips_tiebreak', {
      p_tournament_id: id,
      p_actual: tiebreakActual === '' ? null : Number(tiebreakActual),
    })
    setSavingTiebreak(false)
    if (error) setError(error.message)
  }

  /**
   * Sparar ett fält på en fråga. Skrivs vid blur, inte vid varje tangent —
   * arton frågor gånger fyra fält vore annars en skrivning per bokstav.
   */
  async function saveField(q: Question, field: 'label' | 'opt_1' | 'opt_x' | 'opt_2', value: string) {
    const next = value.trim()
    if (!next || next === q[field]) return

    setError(null)
    const { error } = await supabase
      .from('stryktips_questions')
      .update({ [field]: next })
      .eq('id', q.id)

    if (error) { setError(error.message); void load(); return }

    setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, [field]: next } : x)))
    setSavedQ(q.id)
    setTimeout(() => setSavedQ(null), 1500)
  }

  /** Nästa lediga nummer. Kolumnen är begränsad till 1–18 i schemat. */
  async function addQuestion() {
    const used = new Set(questions.map((q) => q.q_no))
    const next = Array.from({ length: 18 }, (_, i) => i + 1).find((n) => !used.has(n))
    if (!next) { setError('Arton frågor är taket.'); return }

    setError(null)
    const { error } = await supabase.from('stryktips_questions').insert({
      tournament_id: id,
      q_no: next,
      label: `Fråga ${next}`,
      opt_1: '1',
      opt_x: 'X',
      opt_2: '2',
    })

    if (error) setError(error.message)
    else void load()
  }

  /**
   * Tar bort frågan. Facit och samtliga tips på den försvinner genom kaskad,
   * och en trigger räknar om ställningen — utan den hade poängen stått kvar
   * på gamla nivån (0030).
   */
  async function removeQuestion(q: Question) {
    setError(null)
    const { error } = await supabase.from('stryktips_questions').delete().eq('id', q.id)

    setConfirmRemove(null)
    if (error) setError(error.message)
    else void load()
  }

  async function saveTiebreakLabel() {
    setError(null)
    // Texten ligger på tournaments, som bara admin får skriva. Rättaren
    // behöver just den kolumnen — samma skäl som facit-värdet (0024).
    const { error } = await supabase.rpc('set_stryktips_tiebreak_label', {
      p_tournament_id: id,
      p_label: labelDraft.trim() || null,
    })

    if (error) setError(error.message)
    else setTiebreakLabel(labelDraft.trim() || null)
  }

  /**
   * Ersätter frågorna med det inklistrade tipset.
   *
   * Borttagningen först: q_no är unikt per turnering, så en upsert hade
   * krävt att gamla och nya frågor råkar ligga på samma nummer. Facit och
   * allas tips följer med genom kaskad, och triggern i 0030 räknar om
   * ställningen — därför bekräftelsesteget när någon redan tippat.
   */
  async function applyParsed() {
    if (!parsed || parsed.questions.length === 0) return

    setApplying(true)
    setError(null)

    const { error: delErr } = await supabase
      .from('stryktips_questions')
      .delete()
      .eq('tournament_id', id)

    if (delErr) { setError(delErr.message); setApplying(false); return }

    const { error: insErr } = await supabase.from('stryktips_questions').insert(
      parsed.questions.map((q) => ({
        tournament_id: id,
        q_no: q.q_no,
        label: q.label,
        opt_1: q.opt_1,
        opt_x: q.opt_x,
        opt_2: q.opt_2,
      })),
    )

    if (insErr) { setError(insErr.message); setApplying(false); return }

    if (parsed.tiebreakLabel) {
      const { error: tbErr } = await supabase.rpc('set_stryktips_tiebreak_label', {
        p_tournament_id: id,
        p_label: parsed.tiebreakLabel,
      })
      if (tbErr) setError(tbErr.message)
    }

    setApplying(false)
    setConfirmApply(false)
    setParsed(null)
    setPaste('')
    void load()
  }

  async function seedQuestions() {
    setSeeding(true)
    const { error } = await supabase.from('stryktips_questions').insert(
      Array.from({ length: 18 }, (_, i) => ({
        tournament_id: id,
        q_no: i + 1,
        label: `Fråga ${i + 1}`,
        opt_1: '1',
        opt_x: 'X',
        opt_2: '2',
      })),
    )
    setSeeding(false)
    if (error) setError(error.message)
    else void load()
  }

  const answered = questions.filter((q) => q.answer !== null).length

  if (loading) return <div className="page"><p className="muted">Hämtar…</p></div>

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">{tournamentName}</p>
        <h1>Rättarpanelen</h1>
        <p className="muted">
          {answered} av {questions.length} frågor rättade. Ställningen räknas om direkt
          vid varje klick.
        </p>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {questions.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => { setEditing((v) => !v); setConfirmRemove(null) }}>
            {editing ? 'Klar med frågorna' : 'Redigera frågorna'}
          </button>
          {editing && questions.length < 18 && (
            <button onClick={() => void addQuestion()}>Lägg till fråga</button>
          )}
          {editing && (
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              Sparas när du lämnar fältet.
            </span>
          )}
        </div>
      )}

      {editing && entryCount > 0 && (
        <div className="notice">
          {entryCount} {entryCount === 1 ? 'deltagare har' : 'deltagare har'} redan tippat.
          Deras 1/X/2 står kvar när du skriver om en fråga — ändrar du vad alternativen
          betyder ändrar du alltså vad de svarat. Att ta bort en fråga tar bort deras
          tips på den och räknar om ställningen.
        </div>
      )}

      {/* Rättaren äger tipset och måste kunna lägga upp frågorna själv, inte
          bara rätta frågor någon annan skapat. Rättigheten finns i 0024. */}
      {questions.length === 0 && (profile?.role === 'admin' || profile?.role === 'rattare') && (
        <div className="card stack">
          <p className="muted">
            Inga frågor upplagda. Klistra in tipset nedan, eller skapa 18 tomma att
            fylla i för hand.
          </p>
          <div>
            <button onClick={() => void seedQuestions()} disabled={seeding}>
              {seeding ? 'Skapar…' : 'Skapa 18 tomma frågor'}
            </button>
          </div>
        </div>
      )}

      {/* Klistra in hela tipset som det kommer i mejlet. Alternativet var att
          skriva av arton frågor och femtiofyra alternativ för hand, vilket
          ingen gör två gånger. */}
      {(editing || questions.length === 0) && (
        <section className="card stack">
          <h2>Klistra in tipset</h2>
          <p className="muted">
            Klistra in texten som den kommer. Dagrubriker och avdelare hoppas över.
            Frågorna tolkas och visas för granskning innan något sparas.
          </p>

          <textarea
            value={paste}
            onChange={(e) => { setPaste(e.target.value); setParsed(null); setConfirmApply(false) }}
            rows={10}
            placeholder={'Torsdag:\n\n1 Tommy Fleetwood på första hålet…\n1 Träffar både fairway och green\nX Missar både fairway och green\n2 Träffar antingen fairway eller green\n\n…\n\nUtslagsfråga: Vilken placering får Haotong Li?'}
            spellCheck={false}
          />

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setParsed(parseStryktips(paste))} disabled={!paste.trim()}>
              Tolka
            </button>
            {parsed && (
              <button className="link" onClick={() => { setParsed(null); setConfirmApply(false) }}>
                Rensa
              </button>
            )}
          </div>

          {parsed && (
            <div className="stack">
              <p className="muted">
                {parsed.questions.length} frågor tolkade
                {parsed.tiebreakLabel ? ' · utslagsfråga hittad' : ' · ingen utslagsfråga'}
              </p>

              {parsed.warnings.map((w, i) => (
                <div key={i} className="notice">{w}</div>
              ))}

              <div className="board">
                {parsed.questions.map((q) => (
                  <div key={q.q_no} className="tips-preview">
                    <span className="tips-preview__no">{q.q_no}</span>
                    <div>
                      <div className="tips-preview__label">{q.label}</div>
                      <div className="tips-preview__opts">
                        <span><b>1</b> {q.opt_1}</span>
                        <span><b>X</b> {q.opt_x}</span>
                        <span><b>2</b> {q.opt_2}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {parsed.tiebreakLabel && (
                <p className="muted">Utslagsfråga: {parsed.tiebreakLabel}</p>
              )}

              {parsed.questions.length > 0 && (
                confirmApply ? (
                  <div className="stack">
                    <div className="notice notice--error">
                      Ersätter {questions.length > 0 ? `de ${questions.length} befintliga frågorna` : 'frågorna'}
                      {entryCount > 0 && `, samt facit och tipsen från ${entryCount} deltagare`}. Går inte att ångra.
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button className="primary" onClick={() => void applyParsed()} disabled={applying}>
                        {applying ? 'Sparar…' : 'Ja, ersätt frågorna'}
                      </button>
                      <button className="link" onClick={() => setConfirmApply(false)}>Avbryt</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <button className="primary" onClick={() => setConfirmApply(true)}>
                      Använd de här frågorna
                    </button>
                  </div>
                )
              )}
            </div>
          )}
        </section>
      )}

      {questions.map((q) => (
        <div key={q.id} className="card stack" style={{ padding: 16 }}>
          {editing ? (
            <div className="stack">
              <div>
                <label htmlFor={`label-${q.id}`}>Fråga {q.q_no}</label>
                <input
                  id={`label-${q.id}`}
                  type="text"
                  defaultValue={q.label}
                  onBlur={(e) => void saveField(q, 'label', e.target.value)}
                />
              </div>

              <div className="tips-edit">
                {(['opt_1', 'opt_x', 'opt_2'] as const).map((f, i) => (
                  <div key={f}>
                    <label htmlFor={`${f}-${q.id}`}>{OPTIONS[i]}</label>
                    <input
                      id={`${f}-${q.id}`}
                      type="text"
                      defaultValue={q[f]}
                      onBlur={(e) => void saveField(q, f, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {confirmRemove === q.id ? (
                  <>
                    <span className="muted" style={{ fontSize: '0.82rem' }}>
                      Ta bort fråga {q.q_no}, med facit och allas tips på den?
                    </span>
                    <button onClick={() => void removeQuestion(q)}>Ja, ta bort</button>
                    <button className="link" onClick={() => setConfirmRemove(null)}>Avbryt</button>
                  </>
                ) : (
                  <button className="link" onClick={() => setConfirmRemove(q.id)}>
                    Ta bort frågan
                  </button>
                )}
                {savedQ === q.id && <span className="muted" style={{ fontSize: '0.8rem' }}>Sparat</span>}
              </div>
            </div>
          ) : (
            <>
              <div className="tips-question__label">
                <span className="tips-question__no">{q.q_no}</span>
                {q.label}
              </div>
              <div className="tips-question__options">
                {OPTIONS.map((o) => {
                  const text = o === '1' ? q.opt_1 : o === 'X' ? q.opt_x : q.opt_2
                  const chosen = q.answer === o
                  return (
                    <button
                      key={o}
                      type="button"
                      className={`tips-option${chosen ? ' tips-option--facit' : ''}`}
                      onClick={() => void setFacit(q, chosen ? null : o)}
                    >
                      <span className="tips-option__key">{o}</span>
                      <span className="tips-option__text">{text}</span>
                    </button>
                  )
                })}
              </div>
              {q.answer && (
                <button className="link" onClick={() => void setFacit(q, null)}>
                  Ta bort facit
                </button>
              )}
            </>
          )}
        </div>
      ))}

      <section className="card stack">
        <h2>Utslagsfrågan</h2>

        {editing ? (
          <div>
            <label htmlFor="tiebreak-label">Frågans text</label>
            <input
              id="tiebreak-label"
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={() => void saveTiebreakLabel()}
              placeholder="5 japaners totala score mot par"
            />
          </div>
        ) : (
          <p className="muted">{tiebreakLabel ?? 'Ingen utslagsfråga satt för turneringen.'}</p>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 180 }}>
            <label htmlFor="actual">Facit</label>
            <input
              id="actual"
              type="number"
              step="any"
              value={tiebreakActual}
              onChange={(e) => setTiebreakActual(e.target.value)}
            />
          </div>
          <button onClick={() => void saveTiebreak()} disabled={savingTiebreak}>
            {savingTiebreak ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      </section>

      <div>
        <Link to={`/turnering/${id}/stryktips`}>
          <button>Till ställningen</button>
        </Link>
      </div>
    </div>
  )
}
